<script setup lang="ts">
import {
  loadMyPropertyPreferences,
  myPropertyPreferences,
  saveMyPropertyPreferences,
  outputDateFormatValues,
  OutputDateFormat,
} from '@/appLogic/view/preferences';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { openFailToSavedModal, openSuccessToSaveModal } from '@/corePagesModule/components/modal/modalActions';
import { i18n } from '@/i18n';
import SettingsPageTitle from '@/settingsModule/components/SettingsPageTitle.vue';
import { onBeforeMount, ref } from 'vue';

const t = i18n.t;

const selectedFormat = ref<OutputDateFormat | undefined>(undefined);
const isEdited = ref<boolean>(false);

/**
 * 日付フォーマットを変更した際のハンドラ
 * @param format - 選択された日付フォーマット
 */
const onFormatChange = async (format: OutputDateFormat) => {
  isEdited.value = true;
  selectedFormat.value = format;
};

/**
 * 保存ボタンを押した際の処理
 */
const onSave = async () => {
  if (!selectedFormat.value) return;
  isEdited.value = false;
  myPropertyPreferences.outputDateFormat = selectedFormat.value;
  try {
    const res = await waitLoading(saveMyPropertyPreferences(), t('保存中…'), '');
    if (res == null) throw new Error();
    await openSuccessToSaveModal();
  } catch (e) {
    await openFailToSavedModal();
  }
  location.reload();
};

onBeforeMount(async () => {
  await waitLoading(loadMyPropertyPreferences(), undefined, '');
  selectedFormat.value = myPropertyPreferences.outputDateFormat ?? 'YYYY/MM/DD';
  isEdited.value = false;
});
</script>
<template>
  <section class="setting-format-page">
    <header class="setting-format-page__header">
      <SettingsPageTitle :label="t('設定 > 出力日付フォーマット')" />
    </header>
    <div class="container">
      <p class="container__label">{{ t('出力時の日付フォーマットを選択してください') }}</p>
      <div class="container__options">
        <label
          v-for="format in outputDateFormatValues"
          :key="format"
          class="container__option"
          :class="{ 'container__option--selected': selectedFormat === format }"
        >
          <input
            type="radio"
            name="outputDateFormat"
            :value="format"
            :checked="selectedFormat === format"
            class="container__radio"
            @change="onFormatChange(format)"
          />
          <span class="container__format-text">{{ format }}</span>
        </label>
      </div>
      <div class="save">
        <span class="save__text" :class="{ invisible: !isEdited }">{{ t('未保存の設定変更があります') }}</span>
        <TextButton @click="onSave">{{ t('保存する') }}</TextButton>
      </div>
    </div>
  </section>
</template>
<style lang="scss" scoped>
.setting-format-page {
  display: grid;
  gap: 24px;
  padding: 24px;

  &__header {
    display: grid;
    gap: 0;
  }
}
.container {
  background-color: white;
  padding: 24px 16px 40px 18px;
  border-radius: 8px;
  display: grid;
  gap: 16px;
  grid-template-columns: 384px;

  &__label {
    font-size: 14px;
    font-weight: 400;
    color: $textDefault;
    margin: 0;
  }

  &__options {
    display: grid;
    gap: 12px;
  }

  &__option {
    display: grid;
    width: 256px;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    border: 1px solid $borderDefault;
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    &:hover {
      border-color: $semanticPrimary;
    }

    &--selected {
      border-color: $semanticPrimary;
      background-color: $semanticPrimarySubdued;
    }
  }

  &__radio {
    accent-color: $semanticPrimary;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  &__format-text {
    font-size: 14px;
    font-weight: 400;
    color: $textDefault;
  }
}

.save {
  width: 100%;
  padding-top: 32px;
  border-top: 1px solid $borderDefault;
  justify-self: end;
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-items: center;
  &__text {
    font-weight: 700;
    font-size: 14px;
    color: $semanticError;
  }
}
.invisible {
  visibility: hidden;
}
</style>
